import blue from '@mui/material/colors/blue';
import orange from '@mui/material/colors/orange';
import yellow from '@mui/material/colors/yellow';
import { deepmerge } from '@mui/utils';
import { createTheme, type PaletteOptions, type Theme as MuiTheme } from '@mui/material/styles';

export type ColorMode = 'light' | 'dark';

const makePalette = (mode: ColorMode): PaletteOptions => mode === 'dark'
  // Dark mode colors
  ? {
    primary: { main: blue[600]  },
    secondary: { main: blue[200] },
    warning: { main: orange[700] },
    background: {
      default: '#181818',
      paper: '#121212'
    },
    text: {
      primary: 'hsl(0deg 0% 85%)'
    },
    popover: {
      light: '#f0f0f0',
      main: '#d8d8d8',
      dark: '#aaa',
      contrastText: '#222',
    },
    contrastThreshold: 4.5,
  }
  // Light mode colors
  : {
    secondary: { main: blue[200] },
    warning: { main: yellow[500] },
    info: { main: blue[400], light: blue[200] },
    background: {
      default: '#eee',
      paper: '#fff'
    },
    popover: {
      main: '#555'
    },
    contrastThreshold: 4.5,
  };

export const makeTheme = (mode: ColorMode) => {
  const baseTheme = createTheme({
    palette: {
      ...makePalette(mode),
      mode,
      contrastThreshold: 4.5
    },
  });

  const { palette } = baseTheme;

  const backgroundColor = mode === 'dark' ? '#eee' : '#555';

  const components: typeof baseTheme['components'] = {
    MuiTooltip: {
      defaultProps: {
        PopperProps: {
          modifiers: [
            {
              name: 'flip',
              enabled: true,
            },
            {
              name: 'preventOverflow',
              enabled: true,
            },
          ],
        }
      },
      styleOverrides: {
        tooltip:  ({ theme }) => ({
          backgroundColor: theme.palette.popover.main,
          color: theme.palette.popover.contrastText,
          opacity: 1,
          outlineStyle: 'solid',
          outlineWidth: '1px',
          outlineColor: backgroundColor,
          fontSize: '0.9rem'
        }),
        arrow: { color: backgroundColor }
      },
    },
  };

  const typography: Partial<MuiTheme['typography']> = {
    button: {
      textTransform: 'none',
    },
    caption: {
      color: palette.primary.dark,
    }
  };

  return deepmerge(baseTheme, { components, typography });
}

export type Theme = ReturnType<typeof makeTheme>;
